import { useCallback } from 'react';
import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import FieldErrors from './FieldErrors';

export type TextAreaFormFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  onChange?: (value: string) => void;
  onBlur?: () => void;
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
  autoFocus?: boolean;
  placeholder?: string;
  rows?: number;
  dataTestId?: string;
};

export const TextAreaFormField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  onBlur,
  onChange,
  fieldPath,
  control,
  rules,
  autoFocus,
  placeholder,
  rows = 3,
  dataTestId,
}: TextAreaFormFieldProps<TFieldValues, TFieldPath>) => {
  const {
    field: { name, onBlur: innerOnBlur, onChange: innerOnChange, ref, value },
    fieldState: { invalid, error },
  } = useController({ name: fieldPath, control, rules });

  const composedOnBlur = useCallback(() => {
    innerOnBlur();
    if (onBlur) {
      onBlur();
    }
  }, [onBlur, innerOnBlur]);

  const composedOnChange = useCallback(
    (newValue: string) => {
      innerOnChange(newValue);
      onChange?.(newValue);
    },
    [onChange, innerOnChange],
  );

  return (
    <div>
      <textarea
        name={name}
        value={value}
        ref={ref}
        onChange={(e) => composedOnChange(e.target.value)}
        onBlur={composedOnBlur}
        placeholder={placeholder}
        autoFocus={autoFocus}
        rows={rows}
        className='form-textarea'
        data-testid={dataTestId}
      />
      {invalid && error && <FieldErrors error={error} />}
    </div>
  );
};
